import { ref, computed } from 'vue'
import { useUserManagementStore } from '@/stores/userManagementStore'
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { storeToRefs } from 'pinia'
import MiiaFwConfig from '@/configs/config'
import type { UpdateAccountRequestParam } from '@/types/api/dupAuth/updateAccountRequestParam'
import { toDomestic, toInternational } from '@/utils/converter/phoneNumberConverter'
import type { UserManagement } from '@/types/userManagement/userManagement'
import { useCommonModalStore } from '@/stores/commonModalStore'

export const useUserManagement = () => {
  const itemsTyped = computed(() => userList.value)

  const userManagementStore = useUserManagementStore()
  const commonModalStore = useCommonModalStore()
  const { userList } = storeToRefs(userManagementStore)
  const miiaFwStore = useMiiaFwStore()
  const { govCode } = storeToRefs(miiaFwStore)

  /**
   * 自治体管理者用のためのユーザー情報を取得する
   */
  const fetchUserInfoForGovAdmin = async () => {
    await userManagementStore.fetchUserInfoForGovAdmin({
      system: MiiaFwConfig.config.sysId,
      govCode: govCode.value as string
    })
  }

  /**
   * ユーザー情報の特定フィールドを更新する非同期関数。
   *
   * @param rowIndex - 更新対象のユーザーの配列内インデックス
   * @param field - 更新したいフィールド名（UpdateAccountRequestParam のキーのいずれか）
   * @param value - 新しくセットする値（string）
   */
  const updateUserField = async (
    rowIndex: number,
    field: keyof UpdateAccountRequestParam,
    value: string
  ) => {
    const user = itemsTyped.value[rowIndex]
    if (user.userId === undefined) {
      throw new Error('userIdが未定義です')
    }
    // 既存のパラメータをセット
    const paramsBase: UpdateAccountRequestParam = {
      system: MiiaFwConfig.config.sysId,
      userId: user.userId,
      phoneNumber: user.phoneNumber,
      organizationName: user.organizationName,
      deleted: false
    }

    // 動的に値を書き換えた新しいオブジェクトを作成
    const params: UpdateAccountRequestParam = {
      ...paramsBase,
      [field]: value as string // 必要に応じ型アサーション
    }
    await userManagementStore.fetchUpdateUserField(params)
  }

  /**
   * 現在編集中のセル情報を保持するリアクティブ変数
   * rowIndex: 行番号、field: フィールド名
   */
  const editingCell = ref<{ rowIndex: number | null; field: keyof UserManagement | null }>({
    rowIndex: null,
    field: null
  })

  /**
   * 編集中のセルの値を双方向バインドするcomputedプロパティ。
   * 編集対象の行・フィールドが設定されている場合はitemsTypedの該当値を取得・更新し、
   * それ以外は空文字を返す。
   */
  const reactiveFieldValue = computed({
    get() {
      if (editingCell.value.rowIndex !== null && editingCell.value.field) {
        const rawValue = getRawValue()
        // 電話番号フィールドの場合は国内表記に変換して返す
        if (editingCell.value.field === 'phoneNumber' && typeof rawValue === 'string') {
          return toDomestic(rawValue)
        }
        return rawValue
      }
      return ''
    },
    set(newValue) {
      if (editingCell.value.rowIndex !== null && editingCell.value.field !== null) {
        const row = itemsTyped.value[editingCell.value.rowIndex]
        const field = editingCell.value.field

        if (field === 'phoneNumber' && typeof newValue === 'string') {
          // phoneNumberのみ特殊処理（国内表記 → 国際表記変換）
          row[field] = toInternational(newValue)
        } else {
          // それ以外は普通に代入
          row[field] = newValue as string
        }
      }
    }
  })

  /**
   * 編集モード開始時の処理
   * @param rowIndex 編集対象の行番号
   * @param field 編集対象のフィールド名
   */
  function startEdit(rowIndex: number, field: keyof UserManagement) {
    editingCell.value = { rowIndex, field }
  }

  /**
   * 編集モード終了時の処理
   * APIへの更新呼び出しもここで行う
   * @param rowIndex 編集対象の行番号（nullの場合は編集なしとみなす）
   * @param field 編集対象フィールド名（nullの場合は編集なしとみなす）
   */
  async function stopEdit(rowIndex: number | null, field: keyof UserManagement | null) {
    if (rowIndex !== null && field) {
      const typedField = field as keyof UpdateAccountRequestParam
      const value = itemsTyped.value[rowIndex][field] || ''
      // 編集フィールドが団体名
      if (field === 'organizationName' && (value.length < 1 || value.length > 60)) {
        commonModalStore.openAlertModal({ body: '団体名は1～60文字以内で入力してください。' })
        return
      }
      // 編集フィールドが氏名
      if (field === 'userName' && value.length > 60) {
        commonModalStore.openAlertModal({ body: '氏名は60文字以内で入力してください。' })
        return
      }
      await updateUserField(rowIndex, typedField, value)
      // 編集後にユーザー情報を再取得する
      await fetchUserInfoForGovAdmin()
    }
    editingCell.value = { rowIndex: null, field: null }
  }

  /**
   * 指定された編集されているセルの位置に対応するユーザーデータの値を取得する関数
   *
   * @param items - ユーザーデータの配列
   * @returns 指定されたセルの値。`rowIndex` または `field` が `null` の場合は `undefined` を返す
   */
  function getRawValue() {
    if (editingCell.value.rowIndex === null || editingCell.value.field === null) return undefined
    return itemsTyped.value[editingCell.value.rowIndex][editingCell.value.field]
  }

  return {
    itemsTyped,
    fetchUserInfoForGovAdmin,
    updateUserField,
    editingCell,
    reactiveFieldValue,
    startEdit,
    stopEdit
  }
}
